import {
  SMB_FY_QUARTER,
  SMB_PERCENT_FRACTION_DIGITS,
  SMB_QUARTERS,
  SMB_TOTAL_PERCENT,
} from '../configs'
import { ISmbNode, ISmbSplitValue, TSmbSplitStore } from '../model/types'

import { isSmbNodeExcluded } from './isSmbNodeExcluded'

/**
 * Every plan year has entirely independent storage, so editing one year never touches
 * another one.
 */
export const getSmbStoreKey = (nodeId: string, year: number) => `${nodeId}@${year}`

export const getDirectStoredSplit = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number,
  fq: number
): ISmbSplitValue | null => store[getSmbStoreKey(nodeId, year)]?.[fq] || null

export const getStoredSplit = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number,
  fq: number
): ISmbSplitValue | null => {
  const entry = store[getSmbStoreKey(nodeId, year)]
  if (!entry) {
    return null
  }

  const direct = getDirectStoredSplit(store, nodeId, year, fq)
  if (direct) {
    return direct
  }

  // No individual quarter entry yet - the year split applies evenly to every quarter until
  // quarters are overridden one by one, so a parent rolling quarter values up from children
  // planned at year level only still gets a correct weighted figure instead of "no data".
  if (fq !== SMB_FY_QUARTER) {
    return entry[SMB_FY_QUARTER] || null
  }

  return null
}

export const setStoredSplit = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number,
  fq: number,
  smb: number,
  isSeeded = false
): void => {
  const key = getSmbStoreKey(nodeId, year)
  const entry = store[key] || (store[key] = {})
  entry[fq] = { smb, isSeeded }
}

export const deleteStoredSplit = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number,
  fq: number
): void => {
  const key = getSmbStoreKey(nodeId, year)
  const entry = store[key]
  if (!entry) {
    return
  }

  delete entry[fq]
  if (!Object.values(entry).some(Boolean)) {
    delete store[key]
  }
}

export const clearNodeSplits = (store: TSmbSplitStore, nodeId: string, year: number): void => {
  delete store[getSmbStoreKey(nodeId, year)]
}

/**
 * Typing on a row is a deliberate override of everything below it: whatever was planned at
 * the finer grain no longer applies, so it is wiped rather than left stale and disagreeing
 * with the new figure. Scoped to the plan year being edited.
 */
export const clearSubtreeSplits = (store: TSmbSplitStore, node: ISmbNode, year: number): void => {
  node.children.forEach((child) => {
    clearNodeSplits(store, child.id, year)
    clearSubtreeSplits(store, child, year)
  })
}

/**
 * The mirror image of clearSubtreeSplits: an edit also invalidates any direct value still
 * sitting on the node ancestors, so exactly one level of the hierarchy stays authoritative
 * at a time - whichever level was most recently typed into.
 */
export const clearAncestorSplits = (
  store: TSmbSplitStore,
  ancestors: ISmbNode[],
  year: number
): void => {
  ancestors.forEach((ancestor) => clearNodeSplits(store, ancestor.id, year))
}

export const hasGenuineQuarterEntry = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number
): boolean =>
  SMB_QUARTERS.some((fq) => {
    const value = store[getSmbStoreKey(nodeId, year)]?.[fq]

    return !!value && !value.isSeeded
  })

export const hasGenuineDirectSplit = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number
): boolean => {
  const fyValue = store[getSmbStoreKey(nodeId, year)]?.[SMB_FY_QUARTER]

  return (!!fyValue && !fyValue.isSeeded) || hasGenuineQuarterEntry(store, nodeId, year)
}

export const subtreeHasAnySplit = (
  store: TSmbSplitStore,
  node: ISmbNode,
  year: number
): boolean => {
  if (isSmbNodeExcluded(node)) {
    return false
  }

  const entry = store[getSmbStoreKey(node.id, year)]
  if (entry && Object.values(entry).some(Boolean)) {
    return true
  }

  return node.children.some((child) => subtreeHasAnySplit(store, child, year))
}

export const subtreeHasGenuineSplit = (
  store: TSmbSplitStore,
  node: ISmbNode,
  year: number
): boolean =>
  !isSmbNodeExcluded(node) &&
  (hasGenuineDirectSplit(store, node.id, year) ||
    node.children.some((child) => subtreeHasGenuineSplit(store, child, year)))

/**
 * A node with a direct year entry but no quarter entries yet gets all 4 quarters seeded with
 * that same percentage the first time the quarter view is opened - an editable starting
 * point instead of a blank field, without inventing data for never planned nodes.
 */
export const ensureQuarterSeed = (store: TSmbSplitStore, nodeId: string, year: number): void => {
  const entry = store[getSmbStoreKey(nodeId, year)]
  const fyValue = entry?.[SMB_FY_QUARTER]
  if (!fyValue) {
    return
  }

  const hasAnyQuarter = SMB_QUARTERS.some((fq) => !!entry?.[fq])
  if (hasAnyQuarter) {
    return
  }

  SMB_QUARTERS.forEach((fq) => setStoredSplit(store, nodeId, year, fq, fyValue.smb, true))
}

/**
 * Materializes the FY fallback into every still-empty quarter before an actual quarter edit
 * invalidates that FY entry. This preserves the values the user saw in the other quarters.
 */
export const materializeQuarterFallbacks = (
  store: TSmbSplitStore,
  nodeId: string,
  year: number
): void => {
  const fyValue = getDirectStoredSplit(store, nodeId, year, SMB_FY_QUARTER)
  if (!fyValue) {
    return
  }

  SMB_QUARTERS.forEach((fq) => {
    if (!getDirectStoredSplit(store, nodeId, year, fq)) {
      setStoredSplit(store, nodeId, year, fq, fyValue.smb, true)
    }
  })
}

export const normalizeSmbPercent = (value: number) => {
  const factor = 10 ** SMB_PERCENT_FRACTION_DIGITS
  const finiteValue = Number.isFinite(value) ? value : 0
  const clamped = Math.max(0, Math.min(SMB_TOTAL_PERCENT, finiteValue))

  return Math.round(clamped * factor) / factor
}

export const getEuPercent = (smb: number) =>
  normalizeSmbPercent(SMB_TOTAL_PERCENT - normalizeSmbPercent(smb))
